import {
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useConfig } from '@/context/ConfigProvider';
import { useAuthentication } from '@/context/AuthenticationProvider';
import { request } from '@/api/client';
import { ValidationRule } from '@/api/graphql';
import ValidationChip from '@/components/ValidationChip';

/**
 * The rule set, read-only.
 *
 * Worth a screen even without editing, because it is the evidence for the product's central claim:
 * validation is data, not code. An operator can see exactly which rules exist and what each one
 * checks, and an admin can change one without a deploy.
 */
const ValidationRules = () => {
  const config = useConfig();
  const { getIdToken } = useAuthentication();
  const client = useMemo(
    () => ({ endpoint: config.graphqlEndpoint, getIdToken }),
    [config.graphqlEndpoint, getIdToken],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['validationRules'],
    queryFn: () =>
      request<{ validationRules: ValidationRule[] }>(
        client,
        `query {
          validationRules {
            id name field level message checkType checkParams
            appliesToShowType appliesToProvider disabled
          }
        }`,
      ).then((d) => d.validationRules),
  });

  if (isLoading) return <CircularProgress />;
  if (error) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Failed to load rules'}
      </Alert>
    );
  }

  return (
    <>
      <Stack gap={0.5}>
        <Typography variant="h5">Validation rules</Typography>
        <Typography variant="body2" color="text.secondary">
          {data?.length ?? 0} rules, stored as configuration rather than code —
          changing one is a data edit, not a deployment.
        </Typography>
      </Stack>

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Severity</TableCell>
              <TableCell>Field</TableCell>
              <TableCell>Check</TableCell>
              <TableCell>Parameters</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((rule) => (
              <TableRow key={rule.id} sx={{ opacity: rule.disabled ? 0.5 : 1 }}>
                <TableCell>
                  <ValidationChip level={rule.level} count={1} />
                </TableCell>
                <TableCell>{rule.field}</TableCell>
                <TableCell>
                  <code>{rule.checkType}</code>
                </TableCell>
                <TableCell>
                  <code>{rule.checkParams ?? '—'}</code>
                </TableCell>
                <TableCell>
                  {rule.appliesToShowType?.length
                    ? rule.appliesToShowType.join(', ')
                    : rule.appliesToProvider?.length
                    ? rule.appliesToProvider.join(', ')
                    : 'all'}
                </TableCell>
                <TableCell>{rule.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
};

export default ValidationRules;
